import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useResource, formatDate, titleCase } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { EntityForm, Field } from "../../components/EntityForm";
import { companyCreateFields, contactCreateFields, contactOptionsOf, personLabel } from "../../lib/formOptions";
import type { Company, Contact } from "../../lib/types";

export interface Lead {
    id: string;
    title: string;
    stage: "new_inquiry" | "qualified" | "proposal_sent" | "negotiation" | "closed_won" | "closed_lost";
    estimated_value: string | null;
    priority: "low" | "medium" | "high";
    source: string | null;
    service_type: string | null;
    contact_id: string | null;
    company_id: string | null;
    expected_close: string | null;
    notes: string | null;
    created_at: string;
}

/** Stage columns, in pipeline order — same set the Azure CRM used. */
export const STAGES: Array<{ id: Lead["stage"]; label: string }> = [
    { id: "new_inquiry", label: "New Inquiry" },
    { id: "qualified", label: "Qualified" },
    { id: "proposal_sent", label: "Proposal Sent" },
    { id: "negotiation", label: "Negotiation" },
    { id: "closed_won", label: "Closed Won" },
    { id: "closed_lost", label: "Closed Lost" },
];

const currency = (value: string | number | null): string => {
    const n = Number(value ?? 0);
    return n === 0 ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
};

export const Pipeline = () => {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "manager";

    const leads = useResource<Lead[]>("/leads");
    const companies = useResource<Company[]>("/companies");
    const contacts = useResource<Contact[]>("/contacts");

    const [dialog, setDialog] = useState<{ kind: "create" } | { kind: "edit"; lead: Lead } | null>(null);
    const [moving, setMoving] = useState<string | null>(null);

    const companyName = useMemo(() => {
        const map = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string | null) => (id ? map.get(id) ?? "" : "");
    }, [companies.data]);

    const fields: Field[] = [
        { name: "title", label: "Opportunity", kind: "select", required: true,
          optionsFrom: { path: "/opportunity-types", addLabel: "Add opportunity" } },
        { name: "stage", label: "Stage", kind: "select", half: true,
          options: STAGES.map((s) => ({ value: s.id, label: s.label })) },
        { name: "priority", label: "Priority", kind: "select", half: true,
          options: ["low", "medium", "high"].map((p) => ({ value: p, label: titleCase(p) })) },
        { name: "estimated_value", label: "Estimated value (AUD)", kind: "number", min: 0, half: true },
        { name: "expected_close", label: "Expected close", kind: "date", half: true },
        { name: "service_type", label: "Service type", kind: "select", half: true,
          optionsFrom: { path: "/services", addLabel: "Add service" } },
        { name: "source", label: "Lead source", kind: "select", half: true,
          optionsFrom: { path: "/lead-sources", addLabel: "Add lead source" } },
        { name: "company_id", label: "Company", kind: "select", half: true,
          options: (companies.data ?? []).map((c) => ({ value: c.id, label: c.name })),
          addNew: { title: "New organisation", path: "/companies", fields: companyCreateFields() } },
        { name: "contact_id", label: "Contact", kind: "select", half: true,
          cascadeFrom: "company_id",
          options: contactOptionsOf(contacts.data ?? []),
          addNew: { title: "New contact", path: "/contacts",
              fields: contactCreateFields((companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))),
              optionLabel: personLabel,
              inherit: { company_id: "company_id" } },
          help: "Only people at the selected company are offered." },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    const [dragOver, setDragOver] = useState<string | null>(null);

    const setStage = async (lead: Lead, stageId: Lead["stage"]) => {
        if (lead.stage === stageId) return;
        setMoving(lead.id);
        try {
            await api.put(`/leads/${lead.id}`, {
                stage: stageId,
                ...(stageId.startsWith("closed") ? { closed_at: new Date().toISOString() } : {}),
            });
            leads.reload();
        } finally {
            setMoving(null);
        }
    };

    /** Arrow fallback: drag-and-drop is the primary gesture, but keyboards
     *  and screen readers still get first-class stage moves. */
    const move = async (lead: Lead, direction: 1 | -1) => {
        const index = STAGES.findIndex((s) => s.id === lead.stage);
        const next = STAGES[index + direction];
        if (next) await setStage(lead, next.id);
    };

    const all = leads.data ?? [];
    const totalOpen = all
        .filter((l) => !l.stage.startsWith("closed"))
        .reduce((sum, l) => sum + Number(l.estimated_value ?? 0), 0);

    if (leads.error) return <div className="error-banner">{leads.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="New Lead" path="/leads" fields={fields}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); leads.reload(); }} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Edit lead" path="/leads" fields={fields} initial={dialog.lead}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); leads.reload(); }} />
            )}

            <div className="toolbar">
                <span style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                    Open pipeline value: <strong style={{ color: "var(--ink)" }}>{currency(totalOpen)}</strong>
                </span>
                <div className="spacer" />
                {canEdit && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ New Lead</button>
                )}
            </div>

            <div className="pipeline">
                {STAGES.map((stage) => {
                    const inStage = all.filter((l) => l.stage === stage.id);
                    const value = inStage.reduce((sum, l) => sum + Number(l.estimated_value ?? 0), 0);
                    return (
                        <div key={stage.id}
                            className={`pipeline-col ${dragOver === stage.id ? "drag-over" : ""}`}
                            onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDragOver(stage.id); } }}
                            onDragLeave={() => setDragOver((v) => (v === stage.id ? null : v))}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragOver(null);
                                const id = e.dataTransfer.getData("text/lead-id");
                                const lead = all.find((l) => l.id === id);
                                if (lead) void setStage(lead, stage.id);
                            }}>
                            <h3>
                                <span>{stage.label}</span>
                                <span>{inStage.length}{value > 0 ? ` · ${currency(value)}` : ""}</span>
                            </h3>
                            {inStage.map((lead) => (
                                <div key={lead.id}
                                    className={`pipeline-card ${moving === lead.id ? "dragging" : ""}`}
                                    draggable={canEdit}
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData("text/lead-id", lead.id);
                                        e.dataTransfer.effectAllowed = "move";
                                    }}>
                                    <div className="t">{lead.title}</div>
                                    <div className="m">
                                        {companyName(lead.company_id)}
                                        {lead.estimated_value ? ` · ${currency(lead.estimated_value)}` : ""}
                                        {lead.expected_close ? ` · closes ${formatDate(lead.expected_close)}` : ""}
                                    </div>
                                    {canEdit && (
                                        <div className="row-actions" style={{ justifyContent: "flex-start", marginTop: "0.4rem" }}>
                                            <button className="icon-btn" disabled={moving === lead.id || stage.id === "new_inquiry"}
                                                onClick={() => void move(lead, -1)} title="Move back">←</button>
                                            <button className="icon-btn" disabled={moving === lead.id || stage.id === "closed_lost"}
                                                onClick={() => void move(lead, 1)} title="Move forward">→</button>
                                            <button className="icon-btn" onClick={() => setDialog({ kind: "edit", lead })}>Edit</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {inStage.length === 0 && (
                                <p style={{ fontSize: "0.75rem", color: "var(--muted)", textAlign: "center", padding: "1rem 0" }}>
                                    Empty
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
